import type { MentalPokerContextApi } from './mental-poker-context-api';

/**
 * Mental poker (= docs §8.10) の FE 側暗号レイヤ。
 *
 * mixed-mental-poker (WASM) を遅延 import して、 ElGamal commutative shuffle / partial
 * decrypt を JS から呼ぶための薄い wrapper。 鍵 (secret key) は **FE local のみ** に保持し、
 * BE / 観戦者には絶対漏らさない。
 *
 * 設計方針:
 * - すべての値は hex 文字列 (= 32 byte → 64 hex / 64 byte → 128 hex) で授受。
 *   Uint8Array 経由は型変換コスト + serde-wasm-bindgen 経路で複雑になるため避ける。
 * - 鍵生成は (a) 32 byte seed から決定論派生 (= テスト / commit-reveal 経路)、
 *   (b) `crypto.getRandomValues` から random、 の 2 系統。 実運用は (b)。
 * - shuffle / partial decrypt は WASM 内で完結 (= JS は値の橋渡しのみ)。
 */

type Ns = typeof import('../wasm/pkg-mental-poker/mixed_mental_poker');

let mod: Ns | null = null;
let loading: Promise<Ns> | null = null;

async function load(): Promise<Ns> {
  if (mod) return mod;
  if (!loading) {
    loading = (async () => {
      const ns = await import('../wasm/pkg-mental-poker/mixed_mental_poker');
      await ns.default();
      mod = ns;
      return ns;
    })();
  }
  return loading;
}

// ===== 重い shuffle proof は Web Worker で処理 (= メインスレッドのフリーズ回避) =====
// shuffleDeckWithProof / verifyShuffle は cut-and-choose K=64 で各 ~1s かかり、 同期 wasm 呼び
// 出しだと UI を丸ごとブロックする。 これらは sk を使わない純粋関数なので worker に逃がす。
let cryptoWorker: Worker | null = null;
let workerReqId = 0;
const workerPending = new Map<
  number,
  { resolve: (v: unknown) => void; reject: (e: Error) => void }
>();

function getCryptoWorker(): Worker {
  if (!cryptoWorker) {
    cryptoWorker = new Worker(new URL('./poker-crypto.worker.ts', import.meta.url), {
      type: 'module',
    });
    cryptoWorker.addEventListener(
      'message',
      (e: MessageEvent<{ id: number; ok: boolean; result?: unknown; error?: string }>) => {
        const { id, ok, result, error } = e.data;
        const p = workerPending.get(id);
        if (!p) return;
        workerPending.delete(id);
        if (ok) p.resolve(result);
        else p.reject(new Error(error ?? 'crypto worker error'));
      },
    );
    // Worker のインスタンス化失敗 / top-level throw を拾わないと、 pending Promise
    // が永久に settle せずポーカーのハンドが停止する (cf. wasm/cpu.ts の同型実装)。
    // 全 pending を reject し、 worker を捨てて次回再生成できるようにする。
    cryptoWorker.addEventListener('error', (e) => {
      const err = new Error(`crypto worker error: ${e.message ?? '(no message)'}`);
      for (const [id, p] of workerPending) {
        p.reject(err);
        workerPending.delete(id);
      }
      cryptoWorker = null;
    });
  }
  return cryptoWorker;
}

function callWorker<T>(op: string, args?: unknown[]): Promise<T> {
  const id = ++workerReqId;
  return new Promise<T>((resolve, reject) => {
    workerPending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    getCryptoWorker().postMessage(args ? { id, op, args } : { id, op });
  });
}

export async function ensureMentalPoker(): Promise<void> {
  await load();
  // worker 側の wasm も先に温めて初回 shuffle の cold start を避ける (= fire-and-forget)。
  void callWorker('warmup').catch(() => {});
}

/** SHA256 available on HTTP origins too: WebCrypto subtle is not required. */
export async function contextHash(input: Uint8Array): Promise<string> {
  const ns = await load();
  return (ns as typeof ns & MentalPokerContextApi).mpsContextHash(input);
}

export interface KeyPair {
  /** 32 byte hex (= 64 文字)。 FE local 保持のみ、 BE には絶対送らない。 */
  sk: string;
  /** 32 byte hex (= 64 文字、 compressed Ristretto point)。 公開鍵、 broadcast 可。 */
  pk: string;
}

/** 32 byte seed (= hex 64 文字) から決定論的に鍵ペア派生 (= test / 再現性確認用)。
 *  serde_wasm_bindgen::to_value は serde_json::Value::Object を JS Map で返す
 *  ([[project_mixed_game_wasm_serde]] の既知の落とし穴) ので、 ts 側で Map / plain
 *  両対応で正規化する。 */
export async function keypairFromSeed(seedHex: string): Promise<KeyPair> {
  const ns = await load();
  const result = ns.mpsKeypairFromSeed(seedHex) as unknown;
  if (result instanceof Map) {
    const sk = String(result.get('sk') ?? '');
    const pk = String(result.get('pk') ?? '');
    return { sk, pk };
  }
  return result as KeyPair;
}

/** crypto.getRandomValues から本物の random seed を作って鍵ペア派生 (= 実運用)。 */
export async function keypairRandom(): Promise<KeyPair> {
  const seed = new Uint8Array(32);
  crypto.getRandomValues(seed);
  return keypairFromSeed(bytesToHex(seed));
}

// ===== Barnett-Smart full-deck shuffle (= docs §8.10、 衝突 0%) =====

/** 複数公開鍵 hex を合成 (= aggregate ElGamal public key、 順序不問)。 */
export async function combinePks(pksHex: string[]): Promise<string> {
  const ns = await load();
  return ns.mpsCombinePks(pksHex);
}

/** pk_combined + 32 byte seed hex から初期 encrypted deck (= 52 card hex) を作る。
 *  52 枚の distinct card を pk_combined で mask しただけの状態 (= まだ shuffle 前)。 */
export async function initialDeck(
  pkCombinedHex: string,
  seedHex: string,
): Promise<string[]> {
  const ns = await load();
  return ns.mpsInitialDeck(pkCombinedHex, seedHex);
}

export interface ShuffleResult {
  /** shuffle 後の deck (= 52 card hex)。 */
  deck: string[];
  /** この shuffle の検証可能 proof (= JSON 文字列、 verifyShuffle にそのまま渡す)。 */
  proof: string;
}

/** deck に permutation + rerandomize を 1 round 適用 (= 自分の shuffle) し、 同時に
 *  cut-and-choose 検証可能 proof を生成する。 permutation は bijection なので入力 distinct
 *  なら出力も distinct (= 衝突 0%)。 proof は「output が input の正当な permutation+rerand」
 *  を秘密を漏らさず証明し、 相手の選択カード注入を防ぐ (= docs §8.10)。 */
export async function shuffleDeckWithProof(
  deckHex: string[],
  pkCombinedHex: string,
  permSeedHex: string,
  rerandSeedHex: string,
  proofSeedHex: string,
  context?: string,
): Promise<ShuffleResult> {
  // 重い proof 生成は Web Worker で (= メインスレッドを ~1s フリーズさせない)。
  const json = await callWorker<string>(context === undefined ? 'shuffleWithProof' : 'shuffleWithProofContext', [
    deckHex,
    pkCombinedHex,
    permSeedHex,
    rerandSeedHex,
    proofSeedHex,
    ...(context === undefined ? [] : [context]),
  ]);
  const parsed = JSON.parse(json) as { deck: string[]; proof: string };
  return { deck: parsed.deck, proof: parsed.proof };
}

/** 相手の shuffle proof を検証 (= output が input の正当な re-encryption shuffle か)。
 *  false なら相手が permutation でない不正 shuffle (= カード注入等) をした = cheat。 */
export async function verifyShuffle(
  inputDeckHex: string[],
  outputDeckHex: string[],
  pkCombinedHex: string,
  proofJson: string,
  context?: string,
): Promise<boolean> {
  // 重い proof 検証も Web Worker で (= メインスレッドを ~1s フリーズさせない)。
  return Boolean(
    await callWorker<boolean>(context === undefined ? 'verifyShuffle' : 'verifyShuffleContext', [
      inputDeckHex,
      outputDeckHex,
      pkCombinedHex,
      proofJson,
      ...(context === undefined ? [] : [context]),
    ]),
  );
}

export interface PartialShare {
  /** 64 hex compressed Ristretto share (= sk * c1)。 */
  share: string;
  /** 128 hex Chaum-Pedersen DLEQ proof (= 偽 share 検出用)。 */
  proof: string;
}

/** 1 枚の card への partial decryption share + DLEQ proof を作る。
 *  serde_wasm_bindgen の Map 落とし穴 ([[project_mixed_game_wasm_serde]]) に両対応。 */
export async function partialDecryptWithProof(
  cardHex: string,
  skHex: string,
  context?: string,
): Promise<PartialShare> {
  const ns = await load();
  const result = context === undefined ? ns.mpsPartialDecryptWithProof(cardHex, skHex) as unknown : (ns as typeof ns & MentalPokerContextApi).mpsPartialDecryptWithProofContext(cardHex, skHex, context);
  if (result instanceof Map) {
    return {
      share: String(result.get('share') ?? ''),
      proof: String(result.get('proof') ?? ''),
    };
  }
  return result as PartialShare;
}

/** 相手の share + DLEQ proof を検証 (= 偽 share による card 捏造をリアルタイム検出)。 */
export async function verifyPartial(
  cardHex: string,
  pkHex: string,
  shareHex: string,
  proofHex: string,
  context?: string,
): Promise<boolean> {
  const ns = await load();
  return Boolean(context === undefined ? ns.mpsVerifyPartial(cardHex, pkHex, shareHex, proofHex) : (ns as typeof ns & MentalPokerContextApi).mpsVerifyPartialContext(cardHex, pkHex, shareHex, proofHex, context));
}

/** 全 player の share を合成して平文 card index (0..51) を復元。 不足 / 偽 share で null。 */
export async function finalizeCard(
  cardHex: string,
  sharesHex: string[],
): Promise<number | null> {
  const ns = await load();
  const result = ns.mpsFinalizeCard(cardHex, sharesHex);
  return typeof result === 'number' ? result : null;
}

// ===== helpers =====

function bytesToHex(b: Uint8Array): string {
  let s = '';
  for (const byte of b) {
    s += byte.toString(16).padStart(2, '0');
  }
  return s;
}

/** Card index (0..51) を rank/suit に分解 (= engine と一致): rank = 2 + idx/4、 suit = idx % 4。 */
export function cardIndexToRankSuit(idx: number): { rank: number; suit: number } {
  return { rank: 2 + Math.floor(idx / 4), suit: idx % 4 };
}

/** rank (2..14) + suit (0..3) → index (0..51)。 */
export function rankSuitToIndex(rank: number, suit: number): number {
  return (rank - 2) * 4 + suit;
}

